import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { KeyRound, RefreshCw, Shield } from "lucide-react";
import Button from "../ui/Button";
import { USE_MOCK_AUTH } from "../../config/api";
import {
  fetchPaymentGateway,
  refreshPaymentGatewayToken,
} from "../../services/paymentApi";
import { showError, showSuccess } from "../../utils/toast";

export default function PaymentGatewaySettings({ canManage }) {
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const [loading, setLoading] = useState(useLiveApi);
  const [refreshing, setRefreshing] = useState(false);
  const [gateway, setGateway] = useState(null);

  const load = async () => {
    if (!useLiveApi) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchPaymentGateway(token);
      setGateway(data);
    } catch (error) {
      showError(error.message || "Could not load payment gateway settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, useLiveApi]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const result = await refreshPaymentGatewayToken(token);
      setGateway(result?.gateway || result);
      showSuccess("Token regenerated and stored in cache and the database.");
    } catch (error) {
      showError(error.message || "Could not refresh the token.");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="rounded-xl border border-primary/[0.07] bg-white p-5 shadow-sm sm:p-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/[0.06] text-primary">
            <KeyRound className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-base font-bold text-primary">Payment gateway</h2>
            <p className="mt-0.5 text-xs text-primary/45">
              The access token is stored in cache and the database, then regenerated every 23 hours.
            </p>
          </div>
        </div>
        {canManage && useLiveApi ? (
          <Button size="sm" variant="outline" onClick={handleRefresh} loading={refreshing}>
            <RefreshCw className="h-4 w-4" />
            Refresh token
          </Button>
        ) : null}
      </div>

      {loading ? (
        <p className="text-sm text-primary/45">Loading gateway settings…</p>
      ) : (
        <>
          <div className="mb-5 grid gap-3 sm:grid-cols-3">
            <Meta label="Token cached" value={gateway?.has_token ? "Yes" : "No"} />
            <Meta
              label="Token generated"
              value={gateway?.token_generated_at ? String(gateway.token_generated_at).replace("T", " ").slice(0, 16) : "—"}
            />
            <Meta
              label="Expires"
              value={gateway?.token_expires_at ? String(gateway.token_expires_at).replace("T", " ").slice(0, 16) : "—"}
            />
          </div>

          {canManage ? null : (
            <p className="mt-4 flex items-center gap-2 text-xs text-primary/45">
              <Shield className="h-3.5 w-3.5" />
              You can view gateway status. Refreshing the token needs settings.manage.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div className="rounded-lg border border-primary/10 bg-primary/[0.03] px-3 py-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-primary/40">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-primary">{value}</p>
    </div>
  );
}
